// ────────────────────────────────────────────────────────────────────────────
//  BugReport.tsx — Bug-Tracker für Beta-Tester
//
//  Geht direkt nach Login als prominenter Eintrag im Dashboard. User können:
//   - Titel + Beschreibung schreiben
//   - Severity wählen (low/medium/high/critical)
//   - Plattform + App-Version angeben
//   - Screenshots / Videos / PDFs hochladen (Multi, Drag & Drop)
//
//  Backend-Flow:
//   1. Pro Datei → POST /api/v1/bugs/upload (multipart) → R2-URL
//   2. Final POST /api/v1/bugs mit allen Attachment-URLs
//   3. Worker schickt Email an FROM_EMAIL (taironic.media@) für sofortige
//      Dev-Notification
// ────────────────────────────────────────────────────────────────────────────

import { useState, useRef } from 'react'
import { api, getToken, API_ORIGIN } from '../lib/api'
import { useAuth } from '../lib/useAuth'
import { useT } from '../i18n'

type Severity = 'low' | 'medium' | 'high' | 'critical'

interface Attachment {
  url:   string
  name:  string
  type:  string
  bytes: number
}

interface UploadingFile {
  file:    File
  progress: number   // 0..100
  result?:  Attachment
  error?:   string
}

export function BugReport() {
  const { user } = useAuth()
  const t = useT()
  const fileInputRef = useRef<HTMLInputElement>(null)

  const [title,       setTitle]       = useState('')
  const [description, setDescription] = useState('')
  const [severity,    setSeverity]    = useState<Severity>('medium')
  const [platform,    setPlatform]    = useState('web')
  const [appVersion,  setAppVersion]  = useState('')
  const [uploads,     setUploads]     = useState<UploadingFile[]>([])
  const [busy,        setBusy]        = useState(false)
  const [done,        setDone]        = useState(false)
  const [error,       setError]       = useState<string | null>(null)
  const [dragActive,  setDragActive]  = useState(false)

  const allUploaded = uploads.every(u => u.result || u.error)
  const successAttachments = uploads.map(u => u.result).filter(Boolean) as Attachment[]

  const handleFiles = (files: FileList | File[]) => {
    const arr = Array.from(files)
    arr.forEach(file => {
      if (file.size > 50 * 1024 * 1024) {
        setError(t('bug.fileTooBig', { name: file.name }))
        return
      }
      const entry: UploadingFile = { file, progress: 0 }
      setUploads(u => [...u, entry])
      uploadOne(entry)
    })
  }

  const uploadOne = async (entry: UploadingFile) => {
    try {
      const fd = new FormData()
      fd.append('file', entry.file)
      const res = await fetch(`${API_ORIGIN}/api/v1/bugs/upload`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${getToken()}` },
        body: fd,
      })
      const json = await res.json()
      if (!res.ok) throw new Error(json.error ?? `HTTP ${res.status}`)
      setUploads(u => u.map(x => x.file === entry.file ? { ...x, result: json, progress: 100 } : x))
    } catch (e: any) {
      setUploads(u => u.map(x => x.file === entry.file ? { ...x, error: e?.message ?? 'Upload-Fehler', progress: 0 } : x))
    }
  }

  const removeUpload = (file: File) => {
    setUploads(u => u.filter(x => x.file !== file))
  }

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault(); setDragActive(false)
    if (e.dataTransfer.files?.length) handleFiles(e.dataTransfer.files)
  }

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!title.trim()) { setError(t('bug.titleRequired')); return }
    if (!allUploaded)   { setError(t('bug.waitUploads')); return }
    setBusy(true); setError(null)
    try {
      await api.post('/bugs', {
        title:       title.trim(),
        description: description.trim() || undefined,
        severity,
        platform,
        appVersion:  appVersion.trim() || undefined,
        attachments: successAttachments,
      })
      setDone(true)
    } catch (e: any) {
      setError(e?.message ?? 'Senden fehlgeschlagen.')
    } finally {
      setBusy(false)
    }
  }

  if (done) return <SuccessView onAnother={() => {
    setDone(false); setTitle(''); setDescription(''); setUploads([]); setAppVersion('')
  }} />

  return (
    <div className="max-w-3xl mx-auto px-4 md:px-6 py-8 md:py-10">
      <header className="mb-8">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-bold tracking-wider mb-3"
             style={{ background: 'rgba(255,91,92,0.15)', border: '1px solid rgba(255,91,92,0.30)', color: '#ff5b5c' }}>
          <span className="w-1.5 h-1.5 rounded-full bg-coral animate-pulse" />
          {t('bug.kicker')}
        </div>
        <h1 className="text-3xl md:text-4xl font-bold">{t('bug.title')}</h1>
        <p className="mt-2 text-app-text-dim" dangerouslySetInnerHTML={{ __html: t('bug.sub') }} />
      </header>

      <form onSubmit={submit} className="glass p-5 md:p-8 space-y-5">
        <Field label={t('bug.fieldTitle')} required>
          <input type="text" value={title} onChange={e => setTitle(e.target.value)}
            maxLength={140} placeholder={t('bug.titlePlace')}
            className="input-base" required />
        </Field>

        <Field label={t('bug.fieldDesc')}>
          <textarea value={description} onChange={e => setDescription(e.target.value)}
            rows={5} placeholder={t('bug.descPlace')}
            className="input-base font-mono text-sm" />
        </Field>

        <div className="grid md:grid-cols-3 gap-4">
          <Field label={t('bug.severity')}>
            <select value={severity} onChange={e => setSeverity(e.target.value as Severity)} className="input-base">
              <option value="low">{t('bug.sevLow')}</option>
              <option value="medium">{t('bug.sevMid')}</option>
              <option value="high">{t('bug.sevHigh')}</option>
              <option value="critical">{t('bug.sevCrit')}</option>
            </select>
          </Field>
          <Field label={t('bug.platform')}>
            <select value={platform} onChange={e => setPlatform(e.target.value)} className="input-base">
              <option value="ios">{t('bug.platIos')}</option>
              <option value="ipad">{t('bug.platIpad')}</option>
              <option value="mac">{t('bug.platMac')}</option>
              <option value="web">{t('bug.platWeb')}</option>
              <option value="all">{t('bug.platAll')}</option>
            </select>
          </Field>
          <Field label={t('bug.appVersion')}>
            <input type="text" value={appVersion} onChange={e => setAppVersion(e.target.value)}
              placeholder={t('bug.appVerPlace')} className="input-base" />
          </Field>
        </div>

        <Field label={t('bug.attach')}>
          <div
            onDragOver={e => { e.preventDefault(); setDragActive(true) }}
            onDragLeave={() => setDragActive(false)}
            onDrop={onDrop}
            onClick={() => fileInputRef.current?.click()}
            className="relative rounded-xl border-2 border-dashed p-8 text-center cursor-pointer transition"
            style={dragActive
              ? { borderColor: '#8a44ee', background: 'rgba(138,68,238,0.10)' }
              : { borderColor: 'var(--border-strong)', background: 'var(--bg-input)' }}
          >
            <input ref={fileInputRef} type="file" multiple
              onChange={e => e.target.files && handleFiles(e.target.files)}
              className="hidden"
              accept="image/*,video/*,.pdf,.txt,.log,.json,.zip" />
            <div className="text-3xl mb-2">📎</div>
            <div className="font-semibold text-sm">{t('bug.dropZone')}</div>
            <div className="text-xs text-app-text-mute mt-1">{t('bug.dropTypes')}</div>
          </div>
          {uploads.length > 0 && (
            <div className="mt-3 space-y-2">
              {uploads.map((u, i) => <UploadRow key={i} entry={u} onRemove={() => removeUpload(u.file)} t={t} />)}
            </div>
          )}
        </Field>

        {error && (
          <div className="text-sm text-coral rounded-xl p-3"
               style={{ background: 'rgba(255,91,92,0.10)', border: '1px solid rgba(255,91,92,0.30)' }}>
            {error}
          </div>
        )}

        <div className="flex flex-wrap items-center justify-between gap-3 pt-2">
          <div className="text-xs text-app-text-mute">
            {t('bug.reporterLabel')}: <span className="text-app-text-dim">{user?.displayName} · {user?.email}</span>
          </div>
          <button type="submit"
            disabled={busy || !title.trim() || !allUploaded}
            className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed">
            {busy ? t('common.sending') : t('bug.submit')}
          </button>
        </div>
      </form>
    </div>
  )
}

function SuccessView(props: { onAnother: () => void }) {
  const t = useT()
  return (
    <div className="max-w-xl mx-auto px-6 py-20 text-center">
      <div className="glass-strong p-10">
        <div className="text-6xl mb-4">🎉</div>
        <h2 className="text-3xl font-bold mb-2">{t('bug.successTitle')}</h2>
        <p className="text-app-text-dim leading-relaxed mb-8">{t('bug.successText')}</p>
        <button onClick={props.onAnother} className="btn-ghost">
          {t('bug.another')}
        </button>
      </div>
    </div>
  )
}

function UploadRow({ entry, onRemove, t }: { entry: UploadingFile; onRemove: () => void; t: ReturnType<typeof useT> }) {
  const isImg = entry.file.type.startsWith('image/')
  const isVid = entry.file.type.startsWith('video/')
  const sizeKb = (entry.file.size / 1024).toFixed(0)
  return (
    <div className="flex items-center gap-3 px-3 py-2.5 rounded-lg glass-flat">
      <span className="text-xl">{isImg ? '🖼' : isVid ? '🎥' : '📄'}</span>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium truncate">{entry.file.name}</div>
        <div className="text-xs text-app-text-mute">{sizeKb} KB</div>
      </div>
      {entry.error ? (
        <span className="text-xs text-coral font-medium">❌ {entry.error}</span>
      ) : entry.result ? (
        <span className="text-xs text-teal font-medium">{t('bug.uploaded')}</span>
      ) : (
        <span className="text-xs text-app-text-mute">{t('bug.uploading')}</span>
      )}
      <button type="button" onClick={onRemove}
        className="w-7 h-7 rounded-md text-sm transition"
        style={{ background: 'var(--bg-input)', color: 'var(--text-dim)' }}>×</button>
    </div>
  )
}

// ── Form-Field-Wrapper ───────────────────────────────────────────────────
function Field(props: { label: string; required?: boolean; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-[11px] font-bold text-app-text-dim mb-2 tracking-wider uppercase">
        {props.label}{props.required && <span className="text-coral ml-1">*</span>}
      </span>
      {props.children}
    </label>
  )
}
